import React, { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useGetAuxValueGroups } from 'queries';
import { isNil } from 'ramda';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { convertPayloadForSave } from './utils';
import handleSaveTranslations from './utils/handleSaveTranslations';
import mapToOptions from 'utils/mapToOptions';

import { AuxValue } from 'RestAPIs/AuxiliaryValues';
import { AUX_VALUE } from 'RestAPIs/endpoints';
import { useSaveResource } from 'RestAPIs/useSaveResource';

import { LoadingButton } from '@mui/lab';
import Box from '@mui/material/Box';

import { ControlledCheckbox, ControlledSelect } from 'components/shared';
import { FormComponentProps } from 'components/shared/CRUD/TableCRUDLayout';
import { HexColor } from 'components/shared/ColorPickerPopover/ColorPickerPopover';
import TextField from 'components/shared/ControlledFields/ControlledTextField';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import { showSuccessSnackbar } from 'components/shared/Snackbar';

import useGetAuxTranslationsByAuxValueId from '../hooks/useGetAuxTranslationsByAuxValueId';
import TranslationSection from './TranslationSection';
import { schema } from './schema';
import { ControlledColorPickerPopover } from 'components/shared/ControlledFields/ControlledColorPickerPopover';

export interface IAuxValuesFormProps extends FormComponentProps {
  auxValue?: AuxValue;
  refreshList?: () => void;
}

export interface AuxFormValues {
  code: string | number;
  colorCode?: HexColor;
  auxValueGroupId: number;
  auxValueGroupName: string;
  notInUse: boolean;
  translations: { languageCode: string; name: string }[];
}

const AuxValuesForm: React.FC<IAuxValuesFormProps> = ({
  handleClose,
  rowId,
  auxValue
}) => {
  const { t } = useTranslation('common');
  const { data: auxValueGroups } = useGetAuxValueGroups();
  const translations = useGetAuxTranslationsByAuxValueId(rowId);
  const convertedGroups = auxValueGroups?.map((item) => {
    return {
      ...item,
      name: t(`auxGroups.${item.code}`)
    };
  });

  const methods = useForm<AuxFormValues>({
    defaultValues: schema.cast({
      ...auxValue,
      translations: isNil(translations) ? [] : translations
    }),
    resolver: yupResolver(schema)
  });
  const { control, handleSubmit, formState } = methods;

  const { mutateAsync: save, isLoading } = useSaveResource({
    url: AUX_VALUE,
    id: rowId,
    invalidateQueryKey: ['Auxiliary', 'list']
  });

  useEffect(() => {
    if (!isNil(translations)) {
      methods.reset(
        schema.cast({
          ...auxValue,
          translations: isNil(translations) ? [] : translations
        })
      );
    }
  }, [methods.reset, translations]);

  const onSubmit = (data: AuxFormValues) => {
    const payload = convertPayloadForSave(data, auxValueGroups);

    save(payload).then((response) =>
      handleSaveTranslations(
        auxValue?.auxValueId ? auxValue.auxValueId : response.data.auxValueId,
        formState,
        payload.translations
      ).then(() => {
        handleClose();
        showSuccessSnackbar();
      })
    );
  };

  return (
    <>
      <MultiStepForm onSubmit={handleSubmit(onSubmit)} methods={methods}>
        <MultiStepForm.Section
          title={t('auxValues.formSectionLabels.basis')}
          validateFields={['code', 'auxValueGroupName']}
        >
          <Box gap={2.5}>
            <ControlledSelect
              name="auxValueGroupId"
              control={control}
              variant="outlined"
              label={t('auxValues.group')}
              options={mapToOptions(
                convertedGroups ?? [],
                'auxValueGroupId',
                'name'
              )}
            />
          </Box>
          <Box display="flex" gap={2.5}>
            <TextField
              name="code"
              control={control}
              variant="outlined"
              label={t('auxValues.code')}
            />
          </Box>
          <Box display="flex" gap={2.5}>
            <TextField
              name="sortOrder"
              control={control}
              variant="outlined"
              label={t('auxValues.sortOrder')}
            />
          </Box>
          <Box display="flex" gap={2.5}>
            <ControlledCheckbox
              control={control}
              name={`notInUse`}
              label={t('auxValues.notInUse')}
            />
          </Box>
          <Box display="flex" gap={2.5}>
            <ControlledColorPickerPopover
              name="colorCode"
              label={t('auxValues.color')}
            />
          </Box>
        </MultiStepForm.Section>

        <TranslationSection control={control} formState={formState} />

        <MultiStepForm.Actions>
          <Box display="flex" ml="auto">
            <LoadingButton
              loading={isLoading}
              type="submit"
              variant="contained"
              autoFocus
              color="dark"
            >
              {t('save')}
            </LoadingButton>
          </Box>
        </MultiStepForm.Actions>
      </MultiStepForm>
    </>
  );
};

export default AuxValuesForm;
